/**
 * PATH: packages/email-hub/src/feature/email-layout/EmailContent.tsx
 * FILE_ID: EMAIL-LAYOUT-CONTENT-FRAME-001
 * PURPOSE: Assemble folder chrome, connection states, and routed Email content.
 */

import { AlertTriangle } from 'lucide-react'
import type { CSSProperties } from 'react'
import { Outlet } from 'react-router-dom'
import { cn } from '../../_internal/lib/utils'
import EmailConnectionBanner from '../components/EmailConnectionBanner'
import MailboxReconnectBanner from '../components/MailboxReconnectBanner'
import { shouldShowConnectBanner } from '../lib/emailCapability'
import { EmailHubPausedState } from './EmailHubPausedState'
import { MobileFolderSheet } from './MobileFolderSheet'
import type { EmailLayoutController } from './useEmailLayoutController'

function EmailConnectionStates({
  layout,
}: {
  layout: EmailLayoutController
}) {
  const showConnectionBanner = shouldShowConnectBanner({
    quotechAuthLost: layout.quotechAuthLost,
    outlookLoaded: layout.outlook.connected !== null,
    gmailLoaded: layout.gmail.connected !== null,
    outlook: layout.outlook,
    gmail: layout.gmail,
    sharedMailboxes: layout.emailAccounts,
  })

  return (
    <>
      {showConnectionBanner && (
        <div className="px-4 pt-3">
          <EmailConnectionBanner
            outlookStatus={layout.outlook}
            gmailStatus={layout.gmail}
            onConnectOutlook={layout.connectOutlook}
            onConnectGmail={() => {
              void layout.gmail.authorize()
            }}
            compact
          />
        </div>
      )}
      {!layout.quotechAuthLost && (
        <MailboxReconnectBanner
          mailboxes={layout.syncStatus?.mailboxes}
          onReconnectOutlook={(loginHint) => {
            void layout.connectOutlook(loginHint)
          }}
          onReconnectGmail={(loginHint) => {
            void layout.gmail.authorize(loginHint)
          }}
        />
      )}
      {layout.authPaused && !layout.quotechAuthLost && (
        <div className="px-4 pt-3">
          <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-100">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium">
                Sync paused - session token rejected.
              </p>
              <p className="opacity-80 mt-0.5">
                Auto-sync and history backfill are stopped to avoid spamming
                the server with bad credentials. Reconnect Outlook or refresh
                the page to resume.
              </p>
            </div>
            <button
              type="button"
              onClick={() => layout.setAuthPaused(false)}
              className="text-[11px] underline whitespace-nowrap"
              title="Try again - clears the pause without reconnecting"
            >
              Retry now
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export function EmailContent({
  layout,
}: {
  layout: EmailLayoutController
}) {
  return (
    <div
      className={cn(
        'flex-1 flex flex-col min-h-0 min-w-0',
        layout.navOnRight ? 'order-1' : 'order-2',
      )}
    >
      <MobileFolderSheet layout={layout} />
      <EmailConnectionStates layout={layout} />
      <div
        className="flex-1 min-h-0 min-w-0 relative overflow-hidden"
        style={{
          paddingBottom: 'var(--mobile-dock-h, 0px)',
          ...layout.surfaceStyle,
        }}
        data-email-surface={layout.surfaceThemed ? 'themed' : undefined}
        data-email-bg-image={
          layout.surfaceStyle[
            '--email-has-image' as keyof CSSProperties
          ]
            ? '1'
            : undefined
        }
      >
        {layout.quotechAuthLost ? <EmailHubPausedState /> : <Outlet />}
      </div>
    </div>
  )
}
